import { types, flow, getEnv } from 'mobx-state-tree'
import dayjs from 'dayjs'
import months from '../../constants/months'
import GuidePages from './GuidePages'
const { model, optional, maybeNull } = types

// `acceptedCase` and `acceptedContributionDistribution` are declared maybeNull and
// every guide read site guards them, so let those two fetches fail without
// rejecting the batch -- the affected sections hide themselves instead of the whole
// guide collapsing to an error page. The other fetches stay fatal on purpose:
// their stores are read unguarded, so swallowing a failure there would only move
// the crash somewhere else.
const nonFatal = promise =>
  promise.catch(err => {
    console.error(err)
  })

const GuideStore = model({
  status: 'loading',
  error: '',
  guidePages: optional(GuidePages, {}),
  generationDate: maybeNull(types.Date),
})
  .views(self => ({
    get date() {
      const now = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
      }).format(self.generationDate)

      const date = dayjs(now)

      const month = months[date.format('M')]
      const day = date.format('D')
      const year = date.format('YYYY')
      return `${month} ${day}, ${year}`
    },
    get timeStamp() {
      const now = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
        timeZone: 'America/Los_Angeles',
      }).format(self.generationDate)

      const date = dayjs(now)

      const formattedDate = date.format('MMDDYYYY HHmmss')
      return formattedDate + ' PST'
    },
  }))
  .actions(self => ({
    afterCreate: flow(function* () {
      const { store, location, match } = getEnv(self)
      const { caseId } = match.params
      const token = new URLSearchParams(location.search).get('token')

      try {
        if (caseId) {
          store.auth.setGuideToken(token)

          yield store.getConfig()
          yield store.getFeatures()

          if (store.features.enableSpendDown && store.config.spendingMode) {
            self.status = 'done'
            return
          }

          yield Promise.all([
            store.getAccount(),
            store.getAccountInsight(),
            store.getContactInfo(),
            store.getDependents(),
            store.getAccounts(),
            store.getGoals(),
            store.getPerson(),
            store.getSpouse(),
            store.getBaseCase(),
            nonFatal(store.getAcceptedCase(caseId)),
            nonFatal(store.getAcceptedContributionDistribution(caseId)),
            store.getInstitutionalAccount(),
            store.getNonGCAccounts(),
            store.getPortfolioConstruction(),
            store.getPlanOverview(),
            store.getSSBenefits(),
            store.getAllPensions(),
            store.initializeActiveAccount(),
          ])

          self.guidePages.setFetchComplete(true)
          self.generationDate = new Date()
          self.status = 'done'
        } else {
          self.error = 'Missing token or case'
          self.status = 'error'
        }
      } catch (err) {
        // Keep the friendly copy -- `error` is rendered straight into the page, so
        // the serialized error it used to be overwritten with leaked a stack to
        // the participant. Log the real one instead.
        console.error(err)
        self.error = 'Something went wrong, please try again later'
        self.status = 'error'
        // Deliberately not re-thrown: nothing awaits `afterCreate`, so a throw here
        // became an unhandled rejection that the PDF renderer reports as a second
        // page error on top of the error state we just set.
      }
    }),
  }))

export default GuideStore
